import { CircleMarker, Popup } from 'react-leaflet';
import {
  monserratRegular,
  robotoRegular,
  scarpaGray900,
  scarpaGray600,
  scarpaGray500,
  scarpaGray200,
  spanishViridian600,
  spanishViridian900,
  spanishViridian50,
  maximumYellow700,
  electricPurple100,
  electricPurple600,
  tomatoCoral50,
  tomatoCoral600,
} from '@habitech/shared';

import { IComparableRow } from '../../../types';

interface ComparableMarkerProps {
  row: IComparableRow;
  index: number;
}

const formatPrice = (val: number | null): string => {
  if (val == null) return 'N/A';
  return '$' + Math.round(val).toLocaleString('es-MX');
};

const formatNum = (val: number | string | null | undefined): string => {
  if (val == null) return 'N/A';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return 'N/A';
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
};

const formatPct = (val: number | null | undefined): string => {
  if (val == null) return 'N/A';
  return `${(val * 100).toFixed(0)}%`;
};

const priceTypeLabel: Record<string, string> = {
  initial: 'Oferta inicial',
  manual: 'Precio manual',
  precio_propietario: 'Precio propietario',
  avaluo: 'Avalúo',
  oferta_inicial: 'Oferta inicial',
  precio_final: 'Precio final',
};

export const ComparableMarker = ({ row, index }: ComparableMarkerProps) => {
  const { comparable } = row;

  return (
    <CircleMarker
      center={[comparable.latitude as number, comparable.longitude as number]}
      radius={9}
      pathOptions={{
        fillColor: spanishViridian600,
        color: spanishViridian900,
        weight: 2,
        fillOpacity: 0.85,
      }}
    >
      <Popup>
        <div style={{ minWidth: '240px', fontFamily: robotoRegular }}>
          <p style={{
            fontWeight: 600,
            color: spanishViridian600,
            fontSize: '13px',
            marginBottom: '8px',
            borderBottom: `1px solid ${scarpaGray200}`,
            paddingBottom: '6px',
            fontFamily: monserratRegular,
          }}>
            #{index + 1} — {comparable.address || 'Sin dirección'}
          </p>

          <SectionTitle label="Inmueble" />
          <Row label="Área" value={`${formatNum(comparable.area)} m²`} />
          <Row label="Habitaciones" value={formatNum(comparable.room_num)} />
          <Row label="Baños" value={formatNum(comparable.banos)} />
          <Row label="Estacionamiento" value={formatNum(comparable.garage)} />
          {comparable.source_id && (
            <Row label="Fuente" value={comparable.source_id} capitalize />
          )}
          {comparable.date_create && (
            <Row label="Fecha publicación" value={comparable.date_create} />
          )}

          <SectionTitle label="Contexto de pricing" />
          <Row label="Tipo" value={priceTypeLabel[row.price_type] ?? row.price_type} />
          <Row label="Vía" value={row.price_way} capitalize />
          <Row label="Agente" value={row.agente} />
          <Row
            label="Percentil"
            value={
              <span style={{
                background: electricPurple100,
                color: electricPurple600,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: '4px',
                fontSize: '11px',
              }}>
                p{row.percentil}
              </span>
            }
          />
          <Row label="Fecha pricing" value={row.fecha} />

          <SectionTitle label="Precios" />

          <div style={{
            background: spanishViridian50,
            borderRadius: '6px',
            padding: '8px 10px',
            marginBottom: '6px',
          }}>
            <p style={{ fontSize: '10px', color: scarpaGray500, margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Precio normalizado
            </p>
            <p style={{ fontSize: '15px', fontWeight: 700, color: maximumYellow700, margin: '0 0 1px', fontFamily: robotoRegular }}>
              {formatPrice(comparable.norm_price)}
            </p>
            <p style={{ fontSize: '11px', color: scarpaGray500, margin: 0 }}>
              {formatPrice(comparable.norm_price_m2)} / m²
            </p>
          </div>

          <div style={{
            background: tomatoCoral50,
            borderRadius: '6px',
            padding: '8px 10px',
          }}>
            <p style={{ fontSize: '10px', color: scarpaGray500, margin: '0 0 3px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Precio manual · descuento {formatPct(row.discount)}
            </p>
            <p style={{ fontSize: '15px', fontWeight: 700, color: tomatoCoral600, margin: '0 0 1px', fontFamily: robotoRegular }}>
              {formatPrice(row.precio_manual ?? null)}
            </p>
            <p style={{ fontSize: '11px', color: scarpaGray500, margin: 0 }}>
              {formatPrice(row.precio_manual_m2 ?? null)} / m²
            </p>
          </div>

          {row.comment && (
            <p style={{
              fontSize: '11px',
              color: scarpaGray600,
              margin: '8px 0 0',
              padding: '6px 8px',
              background: scarpaGray200 + '40',
              borderRadius: '4px',
              lineHeight: 1.4,
              fontStyle: 'italic',
            }}>
              "{row.comment}"
            </p>
          )}
        </div>
      </Popup>
    </CircleMarker>
  );
};

const SectionTitle = ({ label }: { label: string }) => (
  <p style={{
    fontSize: '10px',
    fontWeight: 700,
    color: scarpaGray500,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    margin: '10px 0 4px',
    fontFamily: robotoRegular,
  }}>
    {label}
  </p>
);

const Row = ({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: string | React.ReactNode;
  capitalize?: boolean;
}) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '11px',
    margin: '3px 0',
    gap: '10px',
    fontFamily: robotoRegular,
  }}>
    <span style={{ color: scarpaGray500 }}>{label}</span>
    <span style={{
      color: scarpaGray900,
      fontWeight: 500,
      textAlign: 'right',
      textTransform: capitalize ? 'capitalize' : 'none',
    }}>
      {value}
    </span>
  </div>
);
